import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  NgZone,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
} from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { APP_PATHS } from '@core/constants/route.constant';
import { ROLE_LAYOUT, conversionFor, seriesColor } from '@core/constants/role-layout.constant';
import {
  browserZone,
  installationZone,
  slotSeriesValue,
  utcIso,
} from '@core/helpers/slot-series.helper';
import { DeviceEnergyflow } from '@core/models/device-energyflow.model';
import { Installation, InstallationTelemetry } from '@core/models/installation.model';
import { ApplicationInsightsService } from '@core/services/application-insights.service';
import { DeviceService } from '@core/services/device.service';
import { TextButtonComponent } from '@custom-components/general/button';
import { CardComponent } from '@custom-components/general/card';
import { CardGridComponent } from '@custom-components/general/card-grid';
import { ButtonToggleComponent } from '@custom-components/general/form/button-toggle/button-toggle.component';
import { ChartSyncService } from '@custom-components/general/chart/chart-sync.service';
import { NoItemsFoundComponent } from '@custom-components/general/no-items-found/no-items-found.component';
import { anchorDayForPeriod } from '@custom-components/sections/insights/performance-ratio/performance-anchor';
import { PerformanceRatioComponent } from '@custom-components/sections/insights/performance-ratio/performance-ratio.component';
import {
  pvPerformanceRatios,
  pvPerformanceTrend,
} from '@custom-components/sections/insights/performance-ratio/performance-ratio.mapper';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DeviceEnergyflowStore } from '@root/app/store/device-energyflow.store';
import { InstallationTelemetryStore } from '@root/app/store/installation-telemetry.store';
import { PvPerformanceStore } from '@root/app/store/pv-performance.store';
import { PeriodTotal, periodTotals, phaseTotalOverlays, voltageLimitOverlay } from './card-extras';
import { energyflowDeviceIds, pvDeviceId } from './device-energyflow';
import { heatpumpSeries } from './heatpump-series';
import {
  buildCardSet,
  formatDisplayValue,
  gridItems,
  hasBarSeries,
  slotLabels,
  unknownRoles,
} from './installation-card-set';
import {
  ChartOverlay,
  PointSeriesChartComponent,
} from './point-series-chart/point-series-chart.component';
import { alignPvForecast, forecastComparison, seriesTotal } from './pv-forecast';
import { catchError, forkJoin, map, of } from 'rxjs';
import {
  SourceStripComponent,
  boundDeviceId,
  latestReadingByPoint,
} from './source-strip/source-strip.component';
import { InstallationInsightsConfigurationComponent } from './configuration/installation-insights-configuration.component';
import { InstallationDataTableComponent } from './data-table/installation-data-table.component';

type ChartType = 'bar' | 'line';

@Component({
  selector: 'xemex-installation-insights-content',
  // Scopes the hover sync to this page, so hovering one chart shows the same slot on every card.
  providers: [ChartSyncService],
  imports: [
    ButtonToggleComponent,
    CardComponent,
    CardGridComponent,
    FormsModule,
    InstallationDataTableComponent,
    InstallationInsightsConfigurationComponent,
    MatIconModule,
    NoItemsFoundComponent,
    PerformanceRatioComponent,
    PointSeriesChartComponent,
    SourceStripComponent,
    TextButtonComponent,
    TranslateModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './installation-insights-content.component.html',
  styleUrl: './installation-insights-content.component.scss',
})
export class InstallationInsightsContentComponent {
  installation = input.required<Installation>();

  readonly telemetryStore = inject(InstallationTelemetryStore);
  readonly pvPerformanceStore = inject(PvPerformanceStore);
  private readonly energyflowStore = inject(DeviceEnergyflowStore);
  private readonly appInsights = inject(ApplicationInsightsService);
  private readonly deviceService = inject(DeviceService);
  private readonly translate = inject(TranslateService);

  readonly points = computed(() => this.installation().points ?? []);
  readonly hasActivePoints = computed(() => this.points().some(point => point.state === 'active'));

  // A point is bound, and a point added, on the data-sources tab.
  readonly dataSourcesLink = computed(() => [
    '/',
    APP_PATHS.Installations,
    this.installation().installationOid,
    'data-sources',
  ]);

  // Thermal energy and the COP have no catalogue role, so they come from each heat pump's
  // energyflow and join the response's own points.
  private readonly heatpump = computed(() =>
    heatpumpSeries(
      this.points(),
      deviceId => this.energyflowFor(deviceId),
      this.telemetryStore.telemetry(),
      key => this.translate.instant(key),
    ),
  );

  readonly cards = computed(() =>
    buildCardSet(
      [...this.points(), ...this.heatpump().points],
      [...(this.telemetryStore.telemetry()?.points ?? []), ...this.heatpump().telemetry],
    ),
  );
  readonly labels = computed(() => slotLabels(this.telemetryStore.telemetry()));

  readonly hasBarSeries = hasBarSeries;
  // Kept here rather than on the chart, so a card keeps its choice when the period changes and the
  // chart is built again. A card without an entry draws its bars as bars.
  private readonly chartTypes = signal<ReadonlyMap<string, ChartType>>(new Map());

  chartTypeOf(cardKey: string): ChartType {
    return this.chartTypes().get(cardKey) ?? 'bar';
  }

  setChartType(cardKey: string, type: string | null): void {
    this.chartTypes.update(types => new Map(types).set(cardKey, type === 'line' ? 'line' : 'bar'));
  }

  readonly timezone = computed(() =>
    installationZone(this.installation().timezone, this.telemetryStore.telemetry()?.timezone),
  );
  readonly showZone = computed(() => this.timezone() !== browserZone());

  private readonly boundDeviceIds = computed(
    () => [
      ...new Set(
        this.points()
          .map(boundDeviceId)
          .filter(deviceId => deviceId !== null),
      ),
    ],
    { equal: (a, b) => a.length === b.length && a.every((id, index) => id === b[index]) },
  );

  // Read once per device, as the device page reads it. A device that cannot be read leaves its
  // points on the newest slot with data, without a snackbar, rather than failing the page.
  private readonly lastReadouts = rxResource({
    params: () => (this.boundDeviceIds().length > 0 ? this.boundDeviceIds() : undefined),
    stream: ({ params }) =>
      forkJoin(
        params.map(deviceId =>
          this.deviceService.getDeviceDetailQuietly(String(deviceId)).pipe(
            map(
              device => [deviceId, device.lastReadout ? utcIso(device.lastReadout) : null] as const,
            ),
            catchError(() => of([deviceId, null] as const)),
          ),
        ),
      ).pipe(map(entries => new Map<number, string | null>(entries))),
  });

  readonly latestReadings = computed(() =>
    this.lastReadouts.isLoading()
      ? undefined
      : latestReadingByPoint(
          this.points(),
          this.telemetryStore.telemetry(),
          this.lastReadouts.value() ?? new Map(),
        ),
  );

  // The ratios and the forecast are still read per device until they are keyed on points.
  private readonly pvDeviceId = computed(() => pvDeviceId(this.points()));

  private readonly alignedForecast = computed(() => {
    const deviceId = this.pvDeviceId();
    const forecast = deviceId ? this.energyflowFor(deviceId) : null;
    return alignPvForecast(forecast, this.telemetryStore.telemetry());
  });

  readonly forecastOverlays = computed<ChartOverlay[]>(() => {
    const forecast = this.alignedForecast();
    if (!forecast) return [];
    const color = seriesColor(ROLE_LAYOUT['pv_production'].colorToken);
    return [
      {
        label: this.translate.instant('installations.insights.forecast.weather'),
        unit: 'Wh',
        values: forecast.weather,
        color,
        borderDash: [6, 4],
      },
      {
        label: this.translate.instant('installations.insights.forecast.clearSky'),
        unit: 'Wh',
        values: forecast.clearSky,
        color,
        borderDash: [2, 3],
      },
    ];
  });

  readonly overlaysByCard = computed(() => {
    const slotCount = this.labels().length;
    const totalLabel = this.translate.instant('installations.insights.activePowerTotal');
    const overlays = new Map<string, ChartOverlay[]>();
    for (const card of this.cards()) {
      const limit = voltageLimitOverlay(card, slotCount);
      overlays.set(card.key, [
        ...(card.key === 'energy' ? this.forecastOverlays() : []),
        ...phaseTotalOverlays(card, slotCount, totalLabel),
        ...(limit ? [limit] : []),
      ]);
    }
    return overlays;
  });

  readonly totalsByCard = computed(() => {
    const totals = new Map<string, PeriodTotal[]>();
    for (const card of this.cards()) {
      const cardTotals = periodTotals(card, key => this.translate.instant(key));
      if (cardTotals.length > 0) {
        totals.set(card.key, cardTotals);
      }
    }
    return totals;
  });

  // Measured PV beside the expected output over the same slots. The forecast adds up every PV
  // channel of one device, so the measurement adds up that device's PV points.
  readonly forecastSummary = computed(() => {
    const forecast = this.alignedForecast();
    const deviceId = this.pvDeviceId();
    const pv = this.cards()
      .flatMap(card => card.series)
      .filter(
        series =>
          series.point.role === 'pv_production' &&
          String(series.point.binding?.source.deviceId) === deviceId &&
          series.telemetry !== null,
      );
    if (!forecast || pv.length === 0) return null;
    const unit = pv[0].point.unit;
    const measured = forecast.weather.map((_, index) =>
      seriesTotal(
        pv
          .filter(series => series.point.unit === unit)
          .map(series => {
            const slot = series.telemetry?.slots[index];
            return slot ? slotSeriesValue(series.point, slot) : null;
          }),
      ),
    );
    const comparison = forecastComparison(measured, forecast.weather);
    if (!comparison) return null;
    return {
      measured: `${formatDisplayValue(comparison.measured, unit)} ${conversionFor(unit).displayUnit}`,
      expected: `${formatDisplayValue(comparison.expected, 'Wh')} ${conversionFor('Wh').displayUnit}`,
    };
  });

  private readonly zone = inject(NgZone);
  private readonly today = signal(new Date());
  private rolloverTimer: ReturnType<typeof setTimeout> | undefined;

  readonly performanceAnchorDate = computed(() =>
    anchorDayForPeriod(this.telemetryStore.selectedPeriod(), this.today()),
  );
  private readonly loadedPerformance = computed(() => {
    const deviceId = this.pvDeviceId();
    if (!deviceId || this.pvPerformanceStore.deviceId() !== deviceId) return null;
    return this.pvPerformanceStore.performance();
  });
  readonly showPerformance = computed(() => this.pvDeviceId() !== null);
  readonly gridItems = computed(() =>
    gridItems(this.cards(), this.installation().profile?.presetCode, this.showPerformance()),
  );
  readonly performanceRatios = computed(() => pvPerformanceRatios(this.loadedPerformance()));
  readonly performanceTrend = computed(() => pvPerformanceTrend(this.loadedPerformance()));

  private readonly reportedRoles = new Set<string>();
  private lastTrackedResponse: InstallationTelemetry | null = null;

  private energyflowFor(deviceId: string): DeviceEnergyflow | null {
    return this.energyflowStore.responseFor(
      deviceId,
      this.telemetryStore.selectedPeriod(),
      this.telemetryStore.telemetryPeriod(),
    );
  }

  constructor() {
    effect(() => {
      const deviceIds = energyflowDeviceIds(this.points());
      const period = this.telemetryStore.selectedPeriod();
      if (deviceIds.length > 0) {
        untracked(() => this.energyflowStore.load({ deviceIds, period }));
      }
    });

    effect(() => {
      const deviceId = this.pvDeviceId();
      const day = this.performanceAnchorDate();
      if (!deviceId || day === null) return;
      this.pvPerformanceStore.getPvPerformance({ deviceId, day });
    });

    // Once per role per page view, so three phase points of one unknown role are one event.
    effect(() => {
      for (const role of unknownRoles(this.points())) {
        if (!this.reportedRoles.has(role)) {
          this.reportedRoles.add(role);
          this.appInsights.trackEvent('role_without_layout', { role_code: role });
        }
      }
    });

    // Once per response, not per render.
    effect(() => {
      const telemetry = this.telemetryStore.telemetry();
      if (!telemetry || telemetry === this.lastTrackedResponse) return;
      this.lastTrackedResponse = telemetry;
      this.appInsights.trackEvent('installation_insights_view', {
        granularity: telemetry.granularity,
        pointCount: telemetry.points.length,
        cardCount: this.cards().length,
      });
    });

    this.scheduleDayRollover();
    inject(DestroyRef).onDestroy(() => clearTimeout(this.rolloverTimer));
  }

  retry(): void {
    this.telemetryStore.fetch();
  }

  // Outside the zone: a timer armed for the next midnight would otherwise keep the zone from
  // ever going stable, which hangs every test that waits for stability.
  private scheduleDayRollover(): void {
    const now = new Date();
    const nextMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    this.zone.runOutsideAngular(() => {
      this.rolloverTimer = setTimeout(() => {
        this.today.set(new Date());
        this.scheduleDayRollover();
      }, nextMidnight.getTime() - now.getTime());
    });
  }
}
